import React from 'react';
import { Skill } from '../../types';

interface SkillBarProps {
  skill: Skill;
  compact?: boolean;
}

export const SkillBar: React.FC<SkillBarProps> = ({ skill, compact = false }) => (
  <div className={`skill-row ${compact ? 'skill-row-compact' : ''}`}>
    <span className="skill-name">{skill.name}</span>
    <div className="skill-track" role="progressbar" aria-valuenow={skill.level} aria-valuemin={1} aria-valuemax={10}>
      <div className="skill-fill" style={{ width: `${skill.level * 10}%` }} />
    </div>
    <span className="skill-level">{skill.level}</span>
  </div>
);

interface SkillEditorProps {
  skills: Skill[];
  onChange: (skills: Skill[]) => void;
  maxSkills?: number;
  onError?: (key: string) => void;
  addLabel?: string;
}

export const SkillEditor: React.FC<SkillEditorProps> = ({
  skills, onChange, maxSkills = 10, onError, addLabel = '+ Add Skill',
}) => {
  const add = () => {
    if (skills.length >= maxSkills) { onError?.('err_max_skills'); return; }
    onChange([...skills, { name: '', level: 5 }]);
  };

  const remove = (i: number) => onChange(skills.filter((_, idx) => idx !== i));

  const update = (i: number, field: keyof Skill, value: string | number) => {
    const updated = skills.map((s, idx) => idx === i ? { ...s, [field]: value } : s);
    onChange(updated);
  };

  return (
    <div className="skill-editor">
      {skills.map((skill, i) => (
        <div key={i} className="skill-editor-row">
          <input
            className="skill-input"
            type="text"
            value={skill.name}
            placeholder="Skill name"
            maxLength={40}
            onChange={(e) => update(i, 'name', e.target.value)}
            aria-label={`Skill ${i + 1} name`}
          />
          <input
            type="range" min={1} max={10} value={skill.level}
            onChange={(e) => update(i, 'level', Number(e.target.value))}
            aria-label={`Skill ${i + 1} level`}
            className="skill-slider"
          />
          <span className="skill-level-badge">{skill.level}</span>
          <button
            type="button"
            className="skill-remove"
            onClick={() => remove(i)}
            aria-label={`Remove ${skill.name}`}
          >✕</button>
        </div>
      ))}
      {skills.length < maxSkills && (
        <button type="button" className="btn btn-outline btn-sm" onClick={add}>
          {addLabel}
        </button>
      )}
      <p className="skill-count">{skills.length}/{maxSkills}</p>
    </div>
  );
};
